import { Dropzone } from "@/view/components/Dropzone";
import { Button } from "@/view/components/ui/button";
import { Download, Paperclip, X } from "lucide-react";
import { AttachmentFormValues } from "../../hooks/useClssForm";
import { extractFileExtension, getFileIcon } from "./helpers";

export const getIconComponent = (fileName: string) => {
  const iconType = getFileIcon(extractFileExtension(fileName));

  const icons: Record<ReturnType<typeof getFileIcon>, React.ReactNode> = {
    image: <img src="/jpeg.png" alt="image" className="h-6 w-6" />,
    "file-text": <img src="/pdf.png" alt="pdf" className="h-6 w-6" />,
    video: <img src="/mov.png" alt="video" className="h-6 w-6" />,
    table: <img src="/xlsx.png" alt="excel" className="h-6 w-6" />,
    file: <img src="/zip.png" alt="file" className="h-6 w-6" />,
  };

  return icons[iconType ?? "file"];
};

const FileItem = ({
  value,
  onDeleteFile,
  isDeletingFile,
}: {
  value: AttachmentFormValues;
  onDeleteFile: (fileName: string, attachmentId: string | undefined) => void;
  isDeletingFile: boolean;
}) => {
  return (
    <div
      key={value.name}
      className="mb-2 flex w-full items-center justify-between rounded-md"
    >
      <div className="flex items-center space-x-2">
        {getIconComponent(value?.name)}
        <p className="max-w-[188px] truncate">{value.name}</p>
      </div>
      <div className="flex items-center text-muted-foreground">
        <span className="mr-2 text-xs">
          {(value?.file?.size / 1024 / 1024).toFixed(2)} MB
        </span>
        <Button
          size="icon"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            const url = URL.createObjectURL(value.file);
            const a = document.createElement("a");
            a.href = url;
            a.download = value?.name;
            a.click();
            URL.revokeObjectURL(url);
          }}
          disabled={isDeletingFile}
          type="button"
        >
          <Download size={24} />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            onDeleteFile(value?.name, value?._id);
          }}
          disabled={isDeletingFile}
          type="button"
        >
          <X size={24} />
        </Button>
      </div>
    </div>
  );
};

export const FileDropzone = ({
  value,
  onChange,
  onDeleteFile,
  isDeletingFile,
}: {
  value: AttachmentFormValues[];
  onChange: (value: AttachmentFormValues[]) => void;
  onDeleteFile: (
    fileName: string,
    attachmentId: string | undefined,
  ) => Promise<void>;
  isDeletingFile: boolean;
}) => {
  if (!value) return;

  const onDrop = (files: File[]) => {
    const newFiles = files.map((file) => ({ file, name: file.name }));
    onChange([...value, ...newFiles]);
  };

  const handleDeleteFile = (
    fileName: string,
    attachmentId: string | undefined,
  ) => {
    onChange(value?.filter((file) => file.name !== fileName) ?? []);
    onDeleteFile(fileName, attachmentId);
  };

  return (
    <Dropzone
      dropzoneOptions={{
        accept: {
          "image/*": [".png", ".jpg", ".jpeg"],
          "application/pdf": [".pdf"],
          "application/msword": [".doc"],
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
            [".docx"],
          "video/*": [".mp4", ".webm", ".ogg"],
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
            ".xlsx",
          ],
        },
        maxFiles: 5,
        maxSize: 5 * 1024 * 1024, // 5 MB,
        multiple: true,
      }}
      onDrop={onDrop}
      value={value.map((file) => file.file)}
      render={() =>
        value?.length ? (
          <>
            {value.map((file) => (
              <FileItem
                key={file.name}
                value={file}
                onDeleteFile={handleDeleteFile}
                isDeletingFile={isDeletingFile}
              />
            ))}
          </>
        ) : (
          <div className="flex w-full items-center gap-4">
            <div className="rounded-md border p-3">
              <Paperclip size={20} />
            </div>
            <p className="text-xs">
              Arraste e solte ou clique aqui para enviar arquivos, você pode
              enviar no máximo 5 arquivos de 5 MB cada
            </p>
          </div>
        )
      }
    />
  );
};
